/**
 * 导航组件
 * 用于应用的主导航菜单
 */

import React from 'react';
import { useAppViewModel } from '../../viewmodels/appviewmodel.jsx';

/**
 * 导航菜单项组件
 * @param {Object} props - 组件属性
 * @param {Object} props.item - 菜单项数据
 * @param {boolean} props.isActive - 是否为当前活跃项
 * @param {Function} props.onClick - 点击回调
 */
function NavigationItem({ item, isActive, onClick }) {
  const { label, icon, href, badge } = item;

  /**
   * 处理点击事件
   * @param {Event} e - 点击事件
   */
  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick(item);
    }
  };

  return (
    <a
      href={href || '#'}
      onClick={handleClick}
      className={`
        flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors
        ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground hover:bg-accent hover:text-accent-foreground'
        }
      `}
      aria-current={isActive ? 'page' : undefined}
    >
      {/* 图标 */}
      {icon && (
        <span className="flex-shrink-0">
          {typeof icon === 'string' ? (
            <span className="text-lg">{icon}</span>
          ) : (
            icon
          )}
        </span>
      )}
      
      {/* 标签 */}
      <span className="flex-1">{label}</span>
      
      {/* 徽章 */}
      {badge && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
          {badge}
        </span>
      )}
    </a>
  );
}

/**
 * 导航分组组件
 * @param {Object} props - 组件属性
 * @param {Object} props.group - 分组数据
 * @param {string} props.activeItem - 当前活跃项
 * @param {Function} props.onItemClick - 项目点击回调
 */
function NavigationGroup({ group, activeItem, onItemClick }) {
  const { title, items } = group;

  return (
    <div className="space-y-1">
      {/* 分组标题 */}
      {title && (
        <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </h3>
      )}
      
      {/* 菜单项列表 */}
      <div className="space-y-1">
        {items.map((item, index) => (
          <NavigationItem
            key={item.id || index}
            item={item}
            isActive={activeItem === item.id}
            onClick={onItemClick}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * 主导航组件
 * @param {Object} props - 组件属性
 * @param {string} props.className - 自定义样式类
 * @param {boolean} props.collapsed - 是否折叠
 */
function Navigation({ className = '' }) {
  const { navigationState, updateNavigationState } = useAppViewModel();
  const { activeItem, menuGroups } = navigationState;

  /**
   * 默认菜单配置
   */
  const defaultMenuGroups = [
    {
      title: '主要功能',
      items: [
        {
          id: 'dashboard',
          label: '仪表板',
          icon: '📊',
          href: '/dashboard'
        },
        {
          id: 'projects',
          label: '项目',
          icon: '📁',
          href: '/projects',
          badge: '3'
        },
        {
          id: 'tasks',
          label: '任务',
          icon: '✅',
          href: '/tasks'
        }
      ]
    },
    {
      title: '工具',
      items: [
        {
          id: 'design-system',
          label: '设计系统',
          icon: '🎨',
          href: '/design-system'
        },
        {
          id: 'components',
          label: '组件库',
          icon: '🧩',
          href: '/components'
        },
        {
          id: 'analytics',
          label: '分析',
          icon: '📈',
          href: '/analytics'
        }
      ]
    },
    {
      title: '设置',
      items: [
        {
          id: 'settings',
          label: '设置',
          icon: '⚙️',
          href: '/settings'
        },
        {
          id: 'help',
          label: '帮助',
          icon: '❓',
          href: '/help'
        }
      ]
    }
  ];

  // 使用默认菜单或从状态获取菜单
  const currentMenuGroups = menuGroups || defaultMenuGroups;

  /**
   * 处理菜单项点击
   * @param {Object} item - 菜单项
   */
  const handleItemClick = (item) => {
    updateNavigationState({
      activeItem: item.id
    });
    
    // 这里可以添加路由跳转逻辑
    console.log('Navigate to:', item.href);
  };

  return (
    <nav className={`space-y-6 ${className}`} aria-label="主导航">
      {currentMenuGroups.map((group, index) => (
        <NavigationGroup
          key={group.title || index}
          group={group}
          activeItem={activeItem}
          onItemClick={handleItemClick}
        />
      ))}
    </nav>
  );
}

/**
 * 面包屑导航组件
 * @param {Object} props - 组件属性
 * @param {Array} props.items - 面包屑项目
 * @param {string} props.className - 自定义样式类
 */
function Breadcrumb({ items = [], className = '' }) {
  if (!items.length) return null;

  return (
    <nav className={`flex ${className}`} aria-label="面包屑">
      <ol className="inline-flex items-center space-x-1 md:space-x-3">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          
          return (
            <li key={index} className="inline-flex items-center">
              {index > 0 && (
                <svg
                  className="w-6 h-6 text-muted-foreground"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              
              {isLast ? (
                <span className="ml-1 text-sm font-medium text-foreground md:ml-2">
                  {item.label}
                </span>
              ) : (
                <a
                  href={item.href || '#'}
                  className="ml-1 text-sm font-medium text-muted-foreground hover:text-foreground md:ml-2"
                >
                  {item.label}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * 标签页导航组件
 * @param {Object} props - 组件属性
 * @param {Array} props.tabs - 标签页数组
 * @param {string} props.activeTab - 当前活跃标签
 * @param {Function} props.onTabChange - 标签切换回调
 * @param {string} props.className - 自定义样式类
 */
function TabNavigation({ tabs = [], activeTab, onTabChange, className = '' }) {
  return (
    <div className={`border-b border-border ${className}`}>
      <nav className="-mb-px flex space-x-8" aria-label="标签页">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange && onTabChange(tab.id)}
              className={`
                whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors
                ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }
              `}
              aria-current={isActive ? 'page' : undefined}
            >
              {tab.icon && (
                <span className="mr-2">{tab.icon}</span>
              )}
              {tab.label}
              {tab.badge && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default Navigation;
export { NavigationItem, NavigationGroup, Breadcrumb, TabNavigation };